import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { GripVertical, ListOrdered, Loader2, RotateCcw } from 'lucide-react';

const DEFAULT_STATUS_ORDER = [
  'Delivered to Customer',
  'On the way to Return',
  'Empty Container in Yard',
  'Empty in Customer',
  'On the way to Customer',
  'In Yard',
  'Delivered to Exam Location',
  'On the way to Pickup',
  'Trouble',
  'Available for Pickup',
  'Need Appt',
  'Important Customer',
  'New Shipment',
  'Container Returned',
  'Shipment Closed'
];

const STATUS_COLORS = {
  'Delivered to Customer': 'bg-green-500/20 border-green-500/50',
  'On the way to Return': 'bg-indigo-500/20 border-indigo-500/50',
  'Empty Container in Yard': 'bg-slate-500/20 border-slate-500/50',
  'Empty in Customer': 'bg-pink-500/20 border-pink-500/50',
  'On the way to Customer': 'bg-orange-500/20 border-orange-500/50',
  'In Yard': 'bg-violet-500/20 border-violet-500/50',
  'Delivered to Exam Location': 'bg-teal-500/20 border-teal-500/50',
  'On the way to Pickup': 'bg-amber-500/20 border-amber-500/50',
  'Trouble': 'bg-rose-500/20 border-rose-500/50',
  'Available for Pickup': 'bg-sky-500/20 border-sky-500/50',
  'Need Appt': 'bg-yellow-500/20 border-yellow-500/50',
  'Important Customer': 'bg-red-500/20 border-red-500/50',
  'New Shipment': 'bg-blue-500/20 border-blue-500/50',
  'Container Returned': 'bg-gray-500/20 border-gray-500/50',
  'Shipment Closed': 'bg-zinc-500/20 border-zinc-500/50'
};

const SortableStatusItem = ({ status, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: status });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-2 rounded-md border ${STATUS_COLORS[status] || 'bg-slate-800 border-slate-700'}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-xs text-slate-400 w-5">{index + 1}.</span>
      <span className="text-sm text-white flex-1">{status}</span>
    </div>
  );
};

import { mergeStatusOrderWithDefault } from './shipmentDashboardHelpers';

export const StatusOrderModal = ({ open, onOpenChange, statusOrder, onSave, loading }) => {
  // iter 264d.16 — always merge saved order with DEFAULT_STATUS_ORDER so
  // when a tenant opens this modal they see EVERY status, including newer
  // ones they never explicitly ordered. Without this, saved orders that
  // predate additions like "On the way to Return" would omit them entirely.
  const [localOrder, setLocalOrder] = useState(mergeStatusOrderWithDefault(statusOrder, DEFAULT_STATUS_ORDER));

  useEffect(() => {
    setLocalOrder(mergeStatusOrderWithDefault(statusOrder, DEFAULT_STATUS_ORDER));
  }, [statusOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = () => {
    onSave(localOrder);
  };

  const handleReset = () => {
    setLocalOrder(DEFAULT_STATUS_ORDER);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800 max-h-[85vh] overflow-hidden flex flex-col" data-testid="status-order-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-white flex items-center gap-2">
            <ListOrdered className="h-5 w-5" />
            Status Display Order
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Drag to reorder how statuses appear in the dashboard. First status appears at the top.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-1.5 max-h-[50vh]">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localOrder}
              strategy={verticalListSortingStrategy}
            >
              {localOrder.map((status, index) => (
                <SortableStatusItem key={status} status={status} index={index} />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter className="flex gap-2 pt-4 border-t border-slate-800">
          <Button
            variant="outline"
            onClick={handleReset}
            className="border-slate-700"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="save-status-order-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StatusOrderModal;
